import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiCamera, FiChevronDown, FiPlus, FiPrinter, FiTrash2 } from 'react-icons/fi';
import { Controller, useWatch } from 'react-hook-form';
import { useToast } from '@/client/hooks/useToast';
import type {
  Control,
  FieldArrayWithId,
  FieldError,
  FieldErrors,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import type { MasterDataFieldDefinition, ImageFieldConfig } from '@/lib/sdk/master-data-field-definitions/types';
import { useCreateImageUploadSession, usePollImageUploadSession } from '@/lib/sdk/image-upload-sessions/queries';

// ─── Per-row key field ────────────────────────────────────────────────────────

interface KeyFieldCellProps {
  idx: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  error?: FieldError;
  knownKeys: string[];
}

function KeyFieldCell({ idx, control, register, setValue, error, knownKeys }: KeyFieldCellProps) {
  const currentValue: string = useWatch({ control, name: `fields.${idx}.k` }) ?? '';

  const isValueCustom = knownKeys.length > 0 && currentValue !== '' && !knownKeys.includes(currentValue);
  const [forceCustom, setForceCustom] = useState(false);

  const showInput = knownKeys.length === 0 || forceCustom || isValueCustom;

  if (showInput) {
    return (
      <FormControl isInvalid={!!error} flex={1}>
        <HStack spacing={1}>
          <Input
            {...register(`fields.${idx}.k`)}
            placeholder="Attribute name"
            size="sm"
            bg="bg-surface"
            borderColor="border-default"
            autoFocus
          />
          {knownKeys.length > 0 && (
            <Button
              size="xs"
              variant="ghost"
              flexShrink={0}
              onClick={() => {
                setForceCustom(false);
                setValue(`fields.${idx}.k`, '');
              }}
            >
              ← List
            </Button>
          )}
        </HStack>
        <FormErrorMessage fontSize="xs">{error?.message}</FormErrorMessage>
      </FormControl>
    );
  }

  return (
    <FormControl isInvalid={!!error} flex={1}>
      <Controller
        control={control}
        name={`fields.${idx}.k`}
        render={({ field }) => (
          <Menu matchWidth>
            <MenuButton
              as={Button}
              size="sm"
              w="full"
              variant="outline"
              fontWeight="normal"
              textAlign="left"
              borderColor={error ? 'red.500' : 'border-default'}
              _hover={{ borderColor: 'border-hover' }}
              _active={{ borderColor: 'brand.500' }}
              rightIcon={<FiChevronDown />}
            >
              <Text as="span" color={field.value ? 'text-primary' : 'text-secondary'} isTruncated>
                {field.value || 'Select attribute…'}
              </Text>
            </MenuButton>
            <MenuList bg="bg-surface" borderColor="border-default" zIndex={10}>
              {knownKeys.map((k) => (
                <MenuItem
                  key={k}
                  bg="bg-surface"
                  _hover={{ bg: 'interactive-hover' }}
                  color="text-primary"
                  fontSize="sm"
                  onClick={() => field.onChange(k)}
                >
                  {k}
                </MenuItem>
              ))}
              <MenuDivider borderColor="border-default" />
              <MenuItem
                bg="bg-surface"
                _hover={{ bg: 'interactive-hover' }}
                color="brand.500"
                fontSize="sm"
                onClick={() => {
                  setForceCustom(true);
                  field.onChange('');
                }}
              >
                ＋ Custom key
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      />
      <FormErrorMessage fontSize="xs">{error?.message}</FormErrorMessage>
    </FormControl>
  );
}

// ─── Value field cell ─────────────────────────────────────────────────────────

interface ValueFieldCellProps {
  idx: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  fieldDefs: MasterDataFieldDefinition[];
  orgId?: string;
  rowId?: number;
  rowKey?: string;
  dataset?: string;
}

interface ImageValueCellProps {
  idx: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  def: MasterDataFieldDefinition;
  orgId: string;
  rowId: number;
  rowKey?: string;
  dataset: string;
}

function ImageValueCell({ idx, control, setValue, def, orgId, rowId, rowKey, dataset }: ImageValueCellProps) {
  const toast = useToast();
  const value: string = useWatch({ control, name: `fields.${idx}.v` }) ?? '';
  const allFields: { k: string; v: string }[] = useWatch({ control, name: 'fields' }) ?? [];

  const [phase, setPhase] = useState<'idle' | 'creating' | 'ready' | 'polling'>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [waUrl, setWaUrl] = useState<string | null>(null);

  const config = def.validation as ImageFieldConfig | null | undefined;

  const storageKey = `img_session:${orgId}:${dataset}:${rowId}:${def.field_name}`;

  // Resume polling if a session was active when the browser was closed
  useEffect(() => {
    if (value) return;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      setSessionId(stored);
      setPhase('polling');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutate: createSession } = useCreateImageUploadSession(orgId);
  const { data: pollData } = usePollImageUploadSession(orgId, sessionId, phase === 'polling');

  useEffect(() => {
    if (pollData?.image_url) {
      setValue(`fields.${idx}.v`, pollData.image_url);
      localStorage.removeItem(storageKey);
      setPhase('idle'); setSessionId(null); setWaUrl(null);
    }
    if (pollData?.expired) {
      localStorage.removeItem(storageKey);
      setPhase('idle'); setSessionId(null); setWaUrl(null);
    }
  }, [pollData, setValue, idx, storageKey]);

  const handleUploadClick = () => {
    if (!config?.chatbot_code) return;
    setPhase('creating');
    const rowFields: Record<string, string> = Object.fromEntries(allFields.map(f => [f.k, f.v]));
    const watermarkText = config.watermark_field
      ? (config.watermark_field === 'key' ? (rowKey ?? '') : (rowFields[config.watermark_field] ?? ''))
      : '';
    createSession(
      { dataset, row_id: rowId, field_name: def.field_name, watermark_text: watermarkText },
      {
        onSuccess: (session) => {
          if (!session.session_id) { setPhase('idle'); return; }
          localStorage.setItem(storageKey, session.session_id);
          const attrs = (config.attribute_mappings ?? [])
            .filter(m => rowFields[m.field_name])
            .map(m => `${m.task_n}:${rowFields[m.field_name]}`)
            .join(' ');
          const parts = [config.chatbot_code, attrs, `upload_ref:${session.session_id}`].filter(Boolean).join(' ');
          const waNumber = (import.meta.env.VITE_APP_WHATSAPP_NUMBER as string | undefined) ?? '';
          setWaUrl(`https://wa.me/${waNumber}?text=${encodeURIComponent(`/start ${parts}`)}`);
          setSessionId(session.session_id);
          setPhase('ready');
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (err: any) => {
          toast.error(err?.message ?? 'Failed to start upload session');
          setPhase('idle');
        },
      }
    );
  };

  const handleCancel = () => {
    localStorage.removeItem(storageKey);
    setPhase('idle'); setSessionId(null); setWaUrl(null);
  };

  return (
    <HStack spacing={2} align="center">
      <Input
        flex={1}
        value={value}
        onChange={(e) => setValue(`fields.${idx}.v`, e.target.value)}
        placeholder="Image URL"
        size="sm"
        bg="bg-surface"
        borderColor="border-default"
        _hover={{ borderColor: 'border-hover' }}
      />
      {config?.chatbot_code && (
        <>
          {phase === 'idle' && value && (
            <Button
              size="xs"
              leftIcon={<FiPrinter size={12} />}
              colorScheme="gray"
              variant="ghost"
              as="a"
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              flexShrink={0}
            >
              Print
            </Button>
          )}
          {phase === 'idle' && !value && (
            <Button size="xs" leftIcon={<FiCamera size={12} />} colorScheme="teal" variant="outline" onClick={handleUploadClick} flexShrink={0}>
              Upload via WhatsApp
            </Button>
          )}
          {phase === 'creating' && (
            <HStack spacing={1} flexShrink={0}>
              <Spinner size="xs" color="brand.500" />
              <Text fontSize="xs" color="text-secondary">Starting...</Text>
            </HStack>
          )}
          {phase === 'ready' && waUrl && (
            <Box
              as="a"
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              display="inline-flex"
              alignItems="center"
              gap={1}
              px={2}
              py={1}
              borderRadius="md"
              bg="teal.500"
              color="white"
              fontSize="xs"
              fontWeight="medium"
              cursor="pointer"
              _hover={{ bg: 'teal.600' }}
              textDecoration="none"
              onClick={() => setPhase('polling')}
              flexShrink={0}
            >
              <FiCamera size={12} />
              Open WhatsApp
            </Box>
          )}
          {phase === 'polling' && (
            <HStack spacing={1} flexShrink={0}>
              <Spinner size="xs" color="brand.500" />
              <Text fontSize="xs" color="text-secondary">Waiting...</Text>
              <Button size="xs" variant="ghost" onClick={handleCancel}>Cancel</Button>
            </HStack>
          )}
        </>
      )}
    </HStack>
  );
}

function ValueFieldCell({ idx, control, register, setValue, fieldDefs, orgId, rowId, rowKey, dataset }: ValueFieldCellProps) {
  const currentKey: string = useWatch({ control, name: `fields.${idx}.k` }) ?? '';
  const def = fieldDefs.find(d => d.field_name === currentKey);

  if (def?.data_type === 'select') {
    return (
      <Select
        {...register(`fields.${idx}.v`)}
        size="sm"
        bg="bg-surface"
        borderColor="border-default"
        _hover={{ borderColor: 'border-hover' }}
      >
        <option value="">—</option>
        {(def.options ?? []).map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </Select>
    );
  }

  if (def?.data_type === 'boolean') {
    return (
      <Select
        {...register(`fields.${idx}.v`)}
        size="sm"
        bg="bg-surface"
        borderColor="border-default"
        _hover={{ borderColor: 'border-hover' }}
      >
        <option value="">—</option>
        <option value="true">True</option>
        <option value="false">False</option>
      </Select>
    );
  }

  if (def?.data_type === 'number') {
    return (
      <Input
        {...register(`fields.${idx}.v`)}
        type="number"
        placeholder="Value"
        size="sm"
        bg="bg-surface"
        borderColor="border-default"
      />
    );
  }

  if (def?.data_type === 'date') {
    return (
      <Input
        {...register(`fields.${idx}.v`)}
        type="date"
        size="sm"
        bg="bg-surface"
        borderColor="border-default"
      />
    );
  }

  if (def?.data_type === 'image' && orgId && rowId !== undefined && dataset) {
    return <ImageValueCell idx={idx} control={control} setValue={setValue} def={def} orgId={orgId} rowId={rowId} rowKey={rowKey} dataset={dataset} />;
  }

  if (def?.data_type === 'image') {
    return (
      <Input
        {...register(`fields.${idx}.v`)}
        placeholder="Image URL"
        size="sm"
        bg="bg-surface"
        borderColor="border-default"
      />
    );
  }

  return (
    <Input
      {...register(`fields.${idx}.v`)}
      placeholder="Value"
      size="sm"
      bg="bg-surface"
      borderColor="border-default"
    />
  );
}

// ─── Fields editor ────────────────────────────────────────────────────────────

interface FieldsEditorProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: FieldArrayWithId<any, 'fields', 'id'>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  append: UseFieldArrayAppend<any, 'fields'>;
  remove: UseFieldArrayRemove;
  knownKeys?: string[];
  fieldDefs?: MasterDataFieldDefinition[];
  orgId?: string;
  rowId?: number;
  rowKey?: string;
  dataset?: string;
  isReadOnly?: boolean;
}

export function FieldsEditor({
  fields,
  register,
  control,
  setValue,
  errors,
  append,
  remove,
  knownKeys = [],
  fieldDefs = [],
  orgId,
  rowId,
  rowKey,
  dataset,
  isReadOnly = false,
}: FieldsEditorProps) {
  return (
    <Box>
      <HStack justify="space-between" mb={1}>
        <Box>
          <Text fontWeight="medium" color="text-primary" fontSize="sm">
            Additional Info
          </Text>
          <Text fontSize="xs" color="text-secondary">
            {isReadOnly
              ? 'Read-only — editing is turned off for this category'
              : knownKeys.length > 0
                ? 'Pick an attribute name from the dropdown, or choose ＋ Custom key to type one'
                : 'Key-value pairs stored with this item (e.g. po_number, quantity)'}
          </Text>
        </Box>
        {!isReadOnly && (
          <Button
            size="xs"
            variant="outline"
            leftIcon={<FiPlus />}
            onClick={() => append({ k: '', v: '' })}
            flexShrink={0}
          >
            Add
          </Button>
        )}
      </HStack>

      {fields.length === 0 ? (
        <Text fontSize="sm" color="text-secondary" py={2} fontStyle="italic">
          {isReadOnly
            ? 'No attributes stored for this item.'
            : 'No attributes yet — click Add to store extra info alongside this item.'}
        </Text>
      ) : (
        // A native disabled fieldset also blocks keyboard focus, which pointer-events alone would not.
        <VStack as="fieldset" disabled={isReadOnly} opacity={isReadOnly ? 0.7 : 1} spacing={2} align="stretch" mt={2}>
          {fields.map((field, idx) => (
            <HStack key={field.id} spacing={2}>
              <KeyFieldCell
                idx={idx}
                control={control}
                register={register}
                setValue={setValue}
                error={errors.fields?.[idx]?.k as FieldError | undefined}
                knownKeys={knownKeys}
              />
              <FormControl flex={2} isInvalid={!!(errors.fields as any)?.[idx]?.v}>
                <ValueFieldCell
                  idx={idx}
                  control={control}
                  register={register}
                  setValue={setValue}
                  fieldDefs={fieldDefs}
                  orgId={orgId}
                  rowId={rowId}
                  rowKey={rowKey}
                  dataset={dataset}
                />
                <FormErrorMessage fontSize="xs">
                  {(errors.fields as any)?.[idx]?.v?.message}
                </FormErrorMessage>
              </FormControl>
              {!isReadOnly && (
                <IconButton
                  aria-label="Remove attribute"
                  icon={<FiTrash2 />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => remove(idx)}
                />
              )}
            </HStack>
          ))}
        </VStack>
      )}
    </Box>
  );
}
